import { usePageBreadcrumbs } from "@/components/layout/breadcrumb-context";
import { useMemo, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { sendSupportRequest } from "@/lib/support-service";
import { trackSupportRequestSent } from "@/lib/analytics-service";
import { useAuthProfile } from "@/hooks/use-auth-profile";
import { Loader2, Mail, Send, AlertCircle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";

export default function SupportPage() {
  const breadcrumbs = useMemo(
    () => [
      { label: "Recipe Boxes", href: "/home" },
      { label: "Support" },
    ],
    [],
  );
  usePageBreadcrumbs(breadcrumbs);

  const { toast } = useToast();
  const { user, profile } = useAuthProfile();
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!subject.trim()) {
      setError("Please enter a subject for your support request.");
      return;
    }

    if (!message.trim()) {
      setError("Please enter a message for your support request.");
      return;
    }

    if (subject.length > 200) {
      setError("Subject must be 200 characters or less.");
      return;
    }

    if (message.length > 5000) {
      setError("Message must be 5000 characters or less.");
      return;
    }

    try {
      setIsSubmitting(true);
      await sendSupportRequest({
        subject: subject.trim(),
        message: message.trim(),
      });

      trackSupportRequestSent();

      toast({
        title: "Support request sent",
        description: "Your support request has been sent successfully. We'll get back to you soon!",
      });

      // Clear form
      setSubject("");
      setMessage("");
    } catch (err) {
      console.error("Failed to send support request", err);
      const errorMessage =
        err instanceof Error
          ? err.message
          : "Failed to send support request. Please try again later.";
      setError(errorMessage);
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const displayName = profile?.displayName || user?.displayName || "User";
  const email = profile?.email || user?.email || "";

  return (
    <div className="container max-w-3xl mx-auto py-6 px-4 space-y-6">
      {/* Header */}
      <div className="space-y-2">
        <div className="flex items-center gap-3">
          <Mail className="h-8 w-8 text-primary" />
          <h1 className="font-headline text-3xl font-semibold tracking-tight">Contact Support</h1>
        </div>
        <p className="text-muted-foreground">
          Have a question or need help? Send us a message and we&apos;ll get back to you as soon as possible.
        </p>
      </div>

      {/* User Info */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Your Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 text-sm">
            <div>
              <span className="font-medium">Name:</span> {displayName}
            </div>
            <div>
              <span className="font-medium">Email:</span> {email}
            </div>
            <p className="text-muted-foreground text-xs mt-2">
              We&apos;ll use this information to respond to your support request.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Support Request Form */}
      <Card>
        <CardHeader>
          <CardTitle>Support Request</CardTitle>
          <CardDescription>
            Please provide as much detail as possible so we can assist you effectively.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="subject">Subject *</Label>
              <Input
                id="subject"
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Brief description of your issue or question"
                maxLength={200}
                disabled={isSubmitting}
                required
              />
              <p className="text-xs text-muted-foreground">
                {subject.length}/200 characters
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="message">Message *</Label>
              <Textarea
                id="message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Please provide details about your issue, question, or feedback..."
                rows={8}
                maxLength={5000}
                disabled={isSubmitting}
                required
              />
              <p className="text-xs text-muted-foreground">
                {message.length}/5000 characters
              </p>
            </div>

            <div className="flex items-center gap-2">
              <Button type="submit" disabled={isSubmitting} className="min-w-[120px]">
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="mr-2 h-4 w-4" />
                    Send Request
                  </>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

